import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Container from '../components/ui/Container';
import ProductCard from '../components/ProductCard';
import NotebookPanel from '../components/ui/NotebookPanel';
import SafeIcon from '../common/SafeIcon';
import Badge from '../components/ui/Badge';
import { products, reviews } from '../data/mockData';

const Home = () => {
  const navigate = useNavigate();
  const featuredProducts = products.filter(p => !p.isBundle).slice(0, 4);
  const bundles = products.filter(p => p.isBundle);

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-sky-blue pt-40 pb-24 md:pt-52 md:pb-36 border-b-[10px] border-ink-navy">
        <div className="absolute top-20 right-[10%] opacity-10 animate-pulse">
          <SafeIcon name="Atom" className="text-[150px] text-white" />
        </div>
        <div className="absolute bottom-10 left-[5%] opacity-10 animate-spin-slow">
          <SafeIcon name="Settings" className="text-[100px] text-white" />
        </div>
        
        <Container className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm border-2 border-white/30 px-4 py-1.5 rounded-full mb-8">
                <Badge variant="yellow" className="animate-bounce">NEW</Badge>
                <span className="text-white font-bold text-sm tracking-wide">Summer Science Series is here!</span>
              </div>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-ink-navy leading-[0.9] mb-8">
                Discover <br/>
                <span className="text-white drop-shadow-[4px_4px_0_#1e1b4b]">Everyday</span> <br/>
                Magic.
              </h1>
              <p className="text-xl md:text-2xl text-ink-navy/80 mb-10 max-w-lg font-bold leading-relaxed">
                Premium STEM kits that bridge the gap between "I don't know" and "I'll find out." 
              </p>
              <div className="flex flex-wrap gap-5">
                <Button onClick={() => navigate('/shop')} size="lg" className="text-lg px-10">
                  Shop All Kits
                </Button>
                <Button onClick={() => navigate('/guide')} variant="outline" size="lg" className="text-lg">
                  Why LittleLab?
                </Button>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="absolute -inset-4 bg-lemon-yellow rounded-[60px] transform -rotate-3 border-4 border-ink-navy"></div>
              <div className="relative z-10 rounded-[60px] border-4 border-ink-navy overflow-hidden shadow-[20px_20px_0_0_#1e1b4b]">
                <img 
                  src="https://images.unsplash.com/photo-1543326727-cf6c39e8f84c?auto=format&fit=crop&w=1000&q=80" 
                  alt="Young scientist exploring" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute bottom-8 left-8 bg-white p-6 rounded-3xl border-4 border-ink-navy max-w-xs shadow-xl">
                  <p className="font-handwriting text-2xl text-ink-navy mb-2 leading-tight">"This is how we learn to see the world!"</p>
                  <p className="text-xs font-black text-sky-blue uppercase tracking-widest">— Dr. Aris, Advisor</p>
                </div>
              </div>
            </motion.div>
          </div>
        </Container>
      </section>

      {/* Trust Badges */}
      <div className="bg-white py-12 border-b-4 border-ink-navy overflow-hidden">
        <Container>
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-10 opacity-50 grayscale hover:grayscale-0 transition-all">
            {['ASTM Certified', 'Non-Toxic Materials', 'Teacher Approved', 'Eco-Packaging', 'Free Local Returns'].map((badge) => (
              <div key={badge} className="flex items-center gap-2 font-black text-sm uppercase tracking-tighter text-ink-navy">
                <div className="w-2 h-2 bg-lemon-yellow rounded-full"></div>
                {badge}
              </div>
            ))}
          </div>
        </Container>
      </div>

      {/* Age Categories - Refined */}
      <section className="py-24 bg-slate-50">
        <Container>
          <div className="text-center mb-16 max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-black text-ink-navy mb-4">Start the Journey</h2>
            <p className="text-slate-500 font-bold text-lg">Every age has its own set of miracles. Choose the workspace that fits best.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { age: '3-5', title: 'Little Builders', color: 'bg-lemon-yellow', icon: 'Box', desc: 'Focus on sensory play and basic structural concepts.' },
              { age: '6-8', title: 'Curious Chemists', color: 'bg-sky-blue', icon: 'Droplet', desc: 'Introduction to reactions, cycles, and observation.' },
              { age: '9-12+', title: 'Future Engineers', color: 'bg-emerald-300', icon: 'Cpu', desc: 'Complex systems, logic circuits, and problem solving.' }
            ].map((cat) => (
              <motion.div 
                key={cat.age}
                whileHover={{ y: -10 }}
                className={`${cat.color} group rounded-[40px] p-10 border-4 border-ink-navy flex flex-col h-full shadow-[10px_10px_0_0_#1e1b4b] cursor-pointer relative overflow-hidden`}
                onClick={() => navigate(`/shop?age=${cat.age}`)}
              >
                <div className="absolute -right-6 -bottom-6 opacity-10 group-hover:scale-125 transition-transform duration-500">
                   <SafeIcon name={cat.icon} className="text-9xl text-ink-navy" />
                </div>
                <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center border-4 border-ink-navy mb-8 shadow-[4px_4px_0_0_#1e1b4b]">
                  <SafeIcon name={cat.icon} className="text-3xl text-ink-navy" />
                </div>
                <h3 className="text-4xl font-black text-ink-navy leading-none mb-2">Ages {cat.age}</h3>
                <p className="text-ink-navy font-extrabold text-xl mb-4">{cat.title}</p>
                <p className="text-ink-navy/70 font-semibold mb-8 flex-grow">{cat.desc}</p>
                <div className="flex items-center font-black text-ink-navy group-hover:gap-4 transition-all">
                  Explore Kits <SafeIcon name="ArrowRight" className="ml-2" />
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </section>

      {/* Featured Grid */}
      <section className="py-24 bg-white">
        <Container>
          <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
            <div className="max-w-xl">
              <h2 className="text-4xl md:text-5xl font-black text-ink-navy mb-4 italic">The Lab Favorites</h2>
              <p className="text-slate-500 font-bold text-lg">Our most reviewed and requested experiences, tested by thousands of young explorers.</p>
            </div>
            <Button onClick={() => navigate('/shop')} variant="outline" className="group">
              View Collection <SafeIcon name="ArrowRight" className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {featuredProducts.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </Container>
      </section>

      {/* Bundle Feature - Notebook Style */}
      <section className="py-24 bg-ink-navy relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-24 bg-white border-b-8 border-ink-navy -skew-y-2 origin-left"></div>
        
        <Container className="pt-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-4">Laboratory Bundles</h2>
            <p className="text-sky-blue font-bold text-xl uppercase tracking-widest">Better Together • Save 15%</p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12">
            {bundles.map(bundle => (
              <NotebookPanel 
                key={bundle.id} 
                title="Lab Notes: Bundle Value"
                annotation="Top Choice!"
                className="hover:scale-[1.02] transition-transform"
              >
                <div className="flex flex-col md:flex-row gap-8 items-start">
                  <div className="w-full md:w-48 h-48 rounded-2xl border-4 border-ink-navy overflow-hidden flex-shrink-0 shadow-lg">
                    <img src={bundle.image} alt={bundle.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-2xl font-black text-ink-navy mb-3">{bundle.name}</h4>
                    <p className="text-slate-600 text-sm mb-6 leading-relaxed font-semibold">
                      {bundle.description} Includes all necessary reagents and a detailed 24-page activity guide.
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-3xl font-black text-ink-navy">${bundle.price}</span>
                        <span className="text-xs font-bold text-sky-500 uppercase">Save $12.00</span>
                      </div>
                      <Button variant="secondary" className="px-8">
                        Claim Bundle
                      </Button>
                    </div>
                  </div>
                </div>
              </NotebookPanel>
            ))}
          </div>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-white">
        <Container>
          <div className="bg-lemon-yellow rounded-[60px] border-4 border-ink-navy p-12 md:p-20 shadow-[20px_20px_0_0_#1e1b4b] relative overflow-hidden">
            <div className="absolute top-0 right-0 p-10 opacity-10">
              <SafeIcon name="Quote" className="text-[200px] text-ink-navy" />
            </div>
            
            <div className="relative z-10 grid md:grid-cols-3 gap-12">
              {reviews.map(review => (
                <div key={review.id} className="flex flex-col">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => <SafeIcon key={i} name="Star" className="text-ink-navy fill-current text-sm" />)}
                  </div>
                  <p className="text-xl font-bold text-ink-navy mb-8 leading-relaxed italic">"{review.text}"</p>
                  <div className="mt-auto flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl border-2 border-ink-navy flex items-center justify-center font-black text-ink-navy shadow-sm">
                      {review.author[0]}
                    </div>
                    <div>
                      <p className="font-black text-ink-navy uppercase text-sm tracking-widest">{review.author}</p>
                      <p className="text-xs font-bold text-ink-navy/60">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-slate-50 border-t-4 border-ink-navy">
        <Container>
          <div className="flex flex-col items-center text-center max-w-3xl mx-auto">
            <div className="w-20 h-20 bg-sky-blue rounded-3xl border-4 border-ink-navy flex items-center justify-center mb-8 shadow-[6px_6px_0_0_#1e1b4b]">
              <SafeIcon name="Heart" className="text-white text-3xl" />
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-ink-navy mb-6">Ready to spark a reaction?</h2>
            <p className="text-xl text-slate-500 font-bold mb-10">Join 10,000+ parents who trust LittleLab for intentional, educational, and safe play.</p>
            <Button size="lg" className="px-12 text-xl">Start Your Experience</Button>
          </div>
        </Container>
      </section>
    </div>
  );
};

export default Home;